import React, { useEffect, useState } from 'react';
import Loader from "../../../components/Loader"; 
import Paginator from "../../../components/Paginator"; 
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link , useParams } from 'react-router-dom';
import DateRangePicker from "../../../components/DateRangePicker";
import {formatDate} from "../../../utils/helper";


const List = () => {
    
    const [isLoader, setIsLoader] = useState(false);
    const [currency] = useState(process.env.REACT_APP_CURRENCY);
    const [categories, setCategories] = useState([]);
    const [pagination, setPagination] = useState({});
    const [apiData, setApiData] = useState({
        search: "",
        daterange: "",
        page: 1,
    });
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);

    // Ordering is only meaningful on an unfiltered list, where a row's position
    // on the page maps to its absolute position in the full ordering.
    const isSortable = !apiData.search && !apiData.daterange;


    useEffect(() => {
        getCategorys();
    }, [apiData.page, apiData.search, apiData.daterange,]); // Dependency array for re-fetching data

    const getCategorys = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/categories",
                data: apiData
            });
            const data = response.data;
            if (data.status) {
                setCategories(data.data);
                setPagination(data.pagination);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
       
    };

    const handlePageChange = (event) => {
        setApiData(prev => ({ ...prev, page: event }));
    };

    const clearFilter = () => {
        setApiData({
            search: '',
            daterange: '',
            page: 1
        });
    };

    const delData = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'you will not be able to revert it!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            const response = await ApiService.request({
                method: "DELETE",
                url: `admin/categories/${id}`,
            });
            const data = response.data;
            if (data.status) {
                getCategorys();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        }
    };

 

    const handleDragStart = (index) => {
        if (!isSortable) return;
        setDragIndex(index);
    };

    const handleDragOver = (event, index) => {
        if (!isSortable || dragIndex === null) return;
        event.preventDefault();
        setOverIndex(index);
    };

    const handleDragEnd = () => {
        setDragIndex(null);
        setOverIndex(null);
    };

    const handleDrop = (event, dropIndex) => {
        if (!isSortable || dragIndex === null) return;
        event.preventDefault();

        if (dragIndex === dropIndex) {
            handleDragEnd();
            return;
        }

        const reordered = [...categories];
        const [moved] = reordered.splice(dragIndex, 1);
        reordered.splice(dropIndex, 0, moved);

        const previous = categories;
        setCategories(reordered);
        handleDragEnd();
        saveOrder(reordered, previous);
    };

    const saveOrder = async (ordered, previous) => {
        // sort_order is absolute across pages, so tell the API where this page starts
        const perPage = pagination?.per_page || ordered.length;
        const offset = ((pagination?.current_page || apiData.page) - 1) * perPage;

        try {
            const response = await ApiService.request({
                method: "POST",
                url: "admin/categories-reorder",
                data: {
                    ids: ordered.map(cat => cat._id),
                    offset,
                },
            });
            const data = response.data;
            if (data.status) {
                toast.success(data.message);
            } else {
                setCategories(previous);
                toast.error(data.message);
            }
        } catch (error) {
            setCategories(previous);
        }
    };

    const updateStatus = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "you want to change its status!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4BC95F',
            confirmButtonText: 'Yes',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            const response = await ApiService.request({
                method: "POST",
                url: `admin/categories/status/${id}`,
            });
            const data = response.data;
            if (data.status) {
                getCategorys();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        }
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0 text-capitalize">
                           Categories</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Categories</li>
                        </ul>
                    </div>
                    <div className="d-flex align-categories-center gap-2 gap-lg-3">
                        {/* Add New categories Button */}
                        <Link to={`/admin/categories/create`}  className='btn btn-base btn-sm'>{("Add New Category")}</Link>
                    </div>
                </div>
            </div>
            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <form method="get">
                                    <div className="row">
                                        <div className="col-md-4">
                                            <div className="d-flex align-categories-center position-relative my-1">
                                                <input type="text" value={apiData.search} onChange={e => setApiData({ ...apiData, search: e.target.value })} className="form-control form-control-solid me-5"
                                                 placeholder={("Search Category title..")} />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <DateRangePicker
                                                value={apiData.daterange}
                                                onChange={(daterange) => setApiData({ ...apiData, daterange, page: 1 })}
                                                placeholder="Select Date Range"
                                            />
                                        </div>
                                        <div className="col-md-3 mt-2">
                                            <button type="button" onClick={clearFilter} className={`btn btn-sm btn-secondary mx-1 ${!apiData.search && !apiData.daterange ? 'd-none' : ''}`}>{("clear")}</button>
                                        </div>
                                        <div className="col-12">
                                            <span className="text-muted fs-7">
                                                {isSortable
                                                    ? 'Drag rows to change the order categories appear in the app.'
                                                    : 'Clear the filters to reorder categories.'}
                                            </span>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="card-body pt-0">
                            <div id="kt_categories_table_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th className="w-40px"></th>
                                                <th>Image</th>
                                                <th>Title</th>
                                                <th>Title Arabic</th>
                                                <th>Created Date</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {categories.length > 0 ? categories.map((cat, index) => (
                                                <tr
                                                    key={cat._id}
                                                    draggable={isSortable}
                                                    onDragStart={() => handleDragStart(index)}
                                                    onDragOver={(e) => handleDragOver(e, index)}
                                                    onDrop={(e) => handleDrop(e, index)}
                                                    onDragEnd={handleDragEnd}
                                                    className={`${dragIndex === index ? 'opacity-50' : ''} ${overIndex === index && dragIndex !== index ? 'bg-light-primary' : ''}`}
                                                >
                                                    <td>
                                                        {isSortable && (
                                                            <i
                                                                className="bi bi-grip-vertical fs-3 text-gray-500"
                                                                style={{ cursor: 'grab' }}
                                                                title="Drag to reorder"
                                                            ></i>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <img src={cat.image} alt="image" className="w-50px h-50px object-cover" />
                                                    </td>
                                                    <td>{cat.title}</td>
                                                    <td>{cat.title_ar}</td>
                                                   
                                                    <td> {formatDate(cat.createdAt,'DD MMM YYYY, hh:mm A')} </td>
                                                    <td>
                                                        <button className="p-0 btn btn-transparent">
                                                            <span className={`badge ${cat.status  ? 'badge-light-success' : 'badge-light-danger'}`} onClick={() => updateStatus(cat._id)} >
                                                                {cat.status  ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </button>
                                                    </td>
                                                    <td className="mt-2">
                                                        <Link to={`/admin/categories/create/${cat._id}`} className="btn btn-sm h-35px btn-light-primary">
                                                            <i className="bi bi-pencil-fill fs-6"></i>
                                                        </Link>
                                                        <button  onClick={() => delData(cat._id)} className="btn btn-sm h-35px btn-light-primary">
                                                            <i className="bi bi-trash-fill fs-6"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="12" className="text-dark text-center mt-2"> No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table> 
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center"  />
                                    )}
                                    <span>Total Records: {pagination?.total_records}</span>


                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
